import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * MultiSelectPopover
 *
 * High-level popover for picking N items from a list with live checkbox
 * toggling. Built on top of `<Popover>` (Radix) and the `<Checkbox>`
 * primitive.
 *
 *   <MultiSelectPopover
 *     items={[{ id: "a", label: "A", swatch: "#abc" }, ...]}
 *     selectedIds={selected}
 *     onChange={setSelected}
 *     header="Pick destinations"
 *   >
 *     <button>{selected.size} selected</button>
 *   </MultiSelectPopover>
 *
 * Selections are live — toggling a checkbox calls `onChange` immediately;
 * there is no implicit Confirm step. Callers that need an explicit
 * confirm flow can pass a `footer` node and own the staging state.
 *
 * Row layout: leading checkbox → optional swatch dot OR icon → label →
 * optional badge. The leading checkbox is the primary affordance; the
 * swatch/icon slot is for additional visual context (e.g. a folder
 * colour or content-type icon). Click anywhere on the row to toggle.
 */
import * as PopoverPrimitive from "@radix-ui/react-popover";
import React, { useCallback, useMemo } from "react";
import { ScrollView } from "../../scroll-view/scroll-view";
import { Checkbox } from "../checkbox/checkbox";
import { PopoverContent } from "../popover/popover";
import "./multi-select-popover.scss";
export const MultiSelectPopover = React.memo(({ children, items, selectedIds, onChange, header, emptyLabel = "Nothing to show", footer, extra, open, onOpenChange, side = "top", align = "start", sideOffset = 6, className, decoration, filter, }) => {
    const toggle = useCallback((id) => {
        const next = new Set(selectedIds);
        if (next.has(id))
            next.delete(id);
        else
            next.add(id);
        onChange(next);
    }, [onChange, selectedIds]);
    const contentClassName = useMemo(() => ["multi-select-popover", className].filter(Boolean).join(" "), [className]);
    return (_jsxs(PopoverPrimitive.Root, { open: open, onOpenChange: onOpenChange, children: [_jsx(PopoverPrimitive.Trigger, { asChild: true, children: children }), _jsxs(PopoverContent, { side: side, align: align, sideOffset: sideOffset, className: contentClassName, children: [decoration, filter !== undefined ? (_jsx("div", { className: "multi-select-popover__filter", children: _jsx("input", { type: "text", className: "multi-select-popover__filter-input", value: filter.value, placeholder: filter.placeholder ?? "Filter…", autoComplete: "off", spellCheck: false, onChange: (e) => filter.onChange(e.target.value), 
                            // Don't let global keyboard handlers steal keystrokes the
                            // user is typing into the filter. Most app-level shortcuts
                            // are gated on `!targetIsEditable`, but stopping
                            // propagation explicitly is a belt-and-braces guard
                            // against any handler that doesn't check.
                            onKeyDown: (e) => e.stopPropagation() }) })) : null, header ? (_jsx("div", { className: "multi-select-popover__header", children: header })) : null, _jsx(ScrollView, { axis: "y", autoHide: "leave", className: "multi-select-popover__scroll", children: _jsx("div", { className: "multi-select-popover__list", role: "listbox", "aria-multiselectable": true, children: items.length === 0 ? (_jsx("div", { className: "multi-select-popover__empty", children: emptyLabel })) : (items.map((item) => {
                                const checked = selectedIds.has(item.id);
                                const isDisabled = item.disabled === true;
                                return (
                                // Row is a `<div>`, not a `<button>`, because the
                                // inner `<Checkbox>` (Radix) renders its own
                                // `<button>` and HTML doesn't allow nested buttons —
                                // browsers silently break the outer button, which
                                // collapses the row's layout. We get the
                                // button-like behaviour via `role="option"` +
                                // explicit click/key handlers.
                                _jsxs("div", { role: "option", "aria-selected": checked, "aria-disabled": isDisabled || undefined, tabIndex: isDisabled ? -1 : 0, className: [
                                        "multi-select-popover__item",
                                        checked && "multi-select-popover__item--checked",
                                        isDisabled && "multi-select-popover__item--disabled",
                                    ]
                                        .filter(Boolean)
                                        .join(" "), onClick: () => {
                                        if (isDisabled)
                                            return;
                                        toggle(item.id);
                                    }, onKeyDown: (e) => {
                                        if (isDisabled)
                                            return;
                                        if (e.key === " " || e.key === "Enter") {
                                            e.preventDefault();
                                            toggle(item.id);
                                        }
                                    }, children: [_jsx(Checkbox, { checked: checked, tabIndex: -1, "aria-hidden": true, className: "multi-select-popover__checkbox", onClick: (e) => e.preventDefault(), onKeyDown: (e) => e.preventDefault() }), item.swatch !== undefined ? (_jsx("span", { className: "multi-select-popover__swatch", style: { background: item.swatch }, "aria-hidden": true })) : item.icon !== undefined ? (_jsx("span", { className: "multi-select-popover__icon", "aria-hidden": true, children: item.icon })) : null, _jsx("span", { className: "multi-select-popover__label", children: item.label }), item.badge ? (_jsx("span", { className: "multi-select-popover__badge", children: item.badge })) : null] }, item.id));
                            })) }) }), extra ? (_jsx("div", { className: "multi-select-popover__extra", children: extra })) : null, footer ? (_jsx("div", { className: "multi-select-popover__footer", children: footer })) : null] })] }));
});
MultiSelectPopover.displayName = "MultiSelectPopover";
